import { Box, Button, CircularProgress, Link, Stack } from '@mui/material'
import { useSnackbar } from 'notistack'
import { FC, useContext, useEffect, useRef, useState } from 'react'
import AppContext from '@/context/appContext'
import { useUserRole } from '@/hooks/useUserRole'
import { customSnackbarSuccessful } from '@/utils/Snackbar/Snackbar'
import { ModalTypes } from '../../../GlobalConfirmationModal/GlobalConfirmationModal'
import { MonitoringPanel } from '../../MonitoringPanel'
import { useStratificationContext } from '../StratificationContext'

const SUPPORT_FORM_URL = 'https://share.hsforms.com/1JLOc-3huQviHqJDhNl7XjQd7qz7'
const DELETE_CLUSTERS_MESSAGE =
  '¿Está seguro de querer eliminar los clusters de este campo? También se eliminarán las áreas de muestreo generadas automáticamente y sus sitios de monitoreo quedarán sin asignar. Las áreas de muestreo manuales no se modifican.'

export const StratificationClusters: FC = () => {
  const {
    clusters,
    isLoadingClusters,
    createClusters,
    deleteClusters,
    isDeletingClusters,
    latestClusterRun,
    isEligibilityCheckRequired,
    hasEligibilityCheck,
    isLoadingEligibilityCheck,
    isRunningEligibility,
    isEligibilityCheckError,
    refetchEligibilityCheck,
    isFetchingEligibilityCheck,
    isMissingExclusionAreas,
  } = useStratificationContext()
  const { setConfirmationModalConfig } = useContext(AppContext)
  const { isAdmin } = useUserRole()
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(false)
  const [showSuccessBanner, setShowSuccessBanner] = useState(false)
  const runningRunIdRef = useRef<string | null>(null)

  const clustersExist = (clusters?.length ?? 0) > 0
  const isRunInProgress = latestClusterRun?.status === 'running'
  // Gated on hasEligibilityCheck alone: isRunningEligibility only picks the alert copy below.
  // The running flag is a localStorage deadline (up to 10 minutes) that outlives the check once
  // it actually lands, so gating on it too would keep the button disabled after a landed result.
  const isAwaitingEligibilityCheck = isEligibilityCheckRequired && !hasEligibilityCheck
  const isDisabled =
    clustersExist ||
    isLoadingClusters ||
    isSubmitting ||
    isRunInProgress ||
    isAwaitingEligibilityCheck
  const isDeleteDisabled =
    !clustersExist || isLoadingClusters || isRunInProgress || isDeletingClusters
  const isIdleWithoutClusters = !clustersExist && !isRunInProgress
  const showEligibilityReason =
    isAwaitingEligibilityCheck &&
    !isLoadingEligibilityCheck &&
    !isEligibilityCheckError &&
    isIdleWithoutClusters
  const showEligibilityCheckErrorReason =
    isAwaitingEligibilityCheck && isEligibilityCheckError && isIdleWithoutClusters
  const showMissingExclusionAreasWarning = isMissingExclusionAreas && isIdleWithoutClusters
  const showFailedRunError = latestClusterRun?.status === 'failed' && !clustersExist
  const showError = submitError || showFailedRunError

  useEffect(() => {
    // Keyed on run id, not just status: a farm switch can jump straight from one farm's running
    // run to a different farm's already-succeeded run, which is not a transition this component
    // observed and must not replay the banner.
    if (
      runningRunIdRef.current === latestClusterRun?.id &&
      latestClusterRun?.status === 'succeeded'
    ) {
      setShowSuccessBanner(true)
    }
    runningRunIdRef.current = latestClusterRun?.status === 'running' ? latestClusterRun.id : null

    // A POST can reject after the server already accepted the run (dropped connection, timeout);
    // once a poll confirms the run reached the server, drop the stale client-side error.
    if (latestClusterRun?.status === 'running') setSubmitError(false)
  }, [latestClusterRun?.status, latestClusterRun?.id])

  const handleClusterize = async () => {
    setSubmitError(false)
    setShowSuccessBanner(false)
    setIsSubmitting(true)
    try {
      await createClusters()
    } catch (error) {
      // Inline banner below is the user-facing surface; the global snackbar
      // would be redundant. Log the underlying error so devs/ops still have
      // a signal when something fails in production.
      console.error('Failed to create farm clusters', error)
      setSubmitError(true)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteClusters = () => {
    setConfirmationModalConfig({
      open: true,
      modalType: ModalTypes.DeleteModalType,
      title: 'Eliminar clusters',
      message: DELETE_CLUSTERS_MESSAGE,
      confirmAction: async () => {
        try {
          await deleteClusters()
        } catch {
          // The global mutation snackbar already reports the failure; the modal awaits this
          // detached from any caller, so rethrowing would only leak an unhandled rejection.
          return
        }
        setShowSuccessBanner(false)
        customSnackbarSuccessful('Clusters eliminados', enqueueSnackbar, closeSnackbar)
      },
    })
  }

  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      <MonitoringPanel.Title>Clúster biofísico</MonitoringPanel.Title>
      <MonitoringPanel.Description>
        Acción manual de única vez sobre el área total.
        <br />
        Aquí se incorporan la textura, elevación, pendiente y NDVI.
        <br />
        Estas capas son permanentes y definen la base de los estratos SOC.
      </MonitoringPanel.Description>

      {showSuccessBanner && (
        <MonitoringPanel.Alert
          severity="success"
          variant="standard"
          onClose={() => setShowSuccessBanner(false)}
        >
          <MonitoringPanel.Alert.Title>¡Clusterización exitosa!</MonitoringPanel.Alert.Title>
        </MonitoringPanel.Alert>
      )}

      {showError && (
        <MonitoringPanel.Alert
          action={
            <Link
              color="inherit"
              href={SUPPORT_FORM_URL}
              rel="noopener noreferrer"
              sx={{ alignSelf: 'center', fontSize: 13, fontWeight: 600 }}
              target="_blank"
              underline="none"
            >
              PEDIR SOPORTE
            </Link>
          }
          severity="error"
          variant="standard"
        >
          <MonitoringPanel.Alert.Title>Error al clusterizar</MonitoringPanel.Alert.Title>
        </MonitoringPanel.Alert>
      )}

      {isRunInProgress && (
        <MonitoringPanel.Alert severity="info" variant="standard">
          <MonitoringPanel.Alert.Title>
            La clusterización está en proceso
          </MonitoringPanel.Alert.Title>
          <MonitoringPanel.Alert.Description>
            Puede tardar unos minutos. Puedes salir de esta pantalla y volver más tarde.
          </MonitoringPanel.Alert.Description>
        </MonitoringPanel.Alert>
      )}

      {showEligibilityCheckErrorReason && (
        <MonitoringPanel.Alert
          action={
            <Button
              color="inherit"
              disabled={isFetchingEligibilityCheck}
              sx={{ alignSelf: 'center', fontSize: 13, fontWeight: 600 }}
              onClick={() => refetchEligibilityCheck()}
            >
              REINTENTAR
            </Button>
          }
          severity="error"
          variant="standard"
        >
          <MonitoringPanel.Alert.Title>
            No pudimos verificar el chequeo de elegibilidad
          </MonitoringPanel.Alert.Title>
        </MonitoringPanel.Alert>
      )}

      {showEligibilityReason && (
        <MonitoringPanel.Alert
          severity={isRunningEligibility ? 'info' : 'warning'}
          variant="standard"
        >
          <MonitoringPanel.Alert.Title>
            {isRunningEligibility
              ? 'El chequeo de elegibilidad está en proceso'
              : 'Debes realizar el chequeo de elegibilidad antes de clusterizar'}
          </MonitoringPanel.Alert.Title>
        </MonitoringPanel.Alert>
      )}

      {showMissingExclusionAreasWarning && (
        <MonitoringPanel.Alert severity="warning" variant="standard">
          <MonitoringPanel.Alert.Title>
            Por favor, asegúrese de agregar todas las áreas de exclusión antes de clusterizar.
          </MonitoringPanel.Alert.Title>
        </MonitoringPanel.Alert>
      )}

      <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5 }}>
        <Button
          color="primary"
          disabled={isDisabled}
          variant="contained"
          onClick={handleClusterize}
        >
          Clusterizar
        </Button>
        {isAdmin && (
          <Button
            color="error"
            disabled={isDeleteDisabled}
            variant="outlined"
            onClick={handleDeleteClusters}
          >
            Eliminar clusters
          </Button>
        )}
        {(isSubmitting || isRunInProgress) && (
          <CircularProgress aria-label="Clusterizando" size={20} />
        )}
      </Box>
    </Stack>
  )
}
